import { baseApi } from "@/app/store/baseApi";
import type {
  ApplicationListItem,
  ApplicationProfile,
  ApplicationProfileUpdateRequest,
  CrifCheckResult,
  CrifDownloadResponse,
  LoanTermsRequest,
  LoanTermsResponse,
  OnboardingOtpVerifyRequest,
  SubmitApplicationRequest,
  SubmitApplicationResponse,
} from "@/features/onboarding/onboarding.types";

export const onboardingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    submitApplication: builder.mutation<SubmitApplicationResponse, SubmitApplicationRequest>({
      query: (payload) => ({ url: "/onboarding/applications", method: "POST", body: payload, auth: true }),
      invalidatesTags: [{ type: "OnboardingApplication", id: "LIST" }],
    }),

    listApplications: builder.query<ApplicationListItem[], void>({
      query: () => ({ url: "/onboarding/applications", auth: true }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((app) => ({ type: "OnboardingApplication" as const, id: app.id })),
              { type: "OnboardingApplication" as const, id: "LIST" },
            ]
          : [{ type: "OnboardingApplication" as const, id: "LIST" }],
    }),

    getApplication: builder.query<ApplicationListItem, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}`, auth: true }),
      providesTags: (result, error, id) => [{ type: "OnboardingApplication", id }],
    }),

    // 204, no body — reveals the OTP entry step once it resolves.
    sendVerificationOtp: builder.mutation<void, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/verify/otp/send`, method: "POST", auth: true }),
    }),

    // Response is the updated application (fresh state -> VERIFIED,
    // isVerified -> true) — invalidate that one application's tag so
    // ApplicationReviewFlow's useGetApplicationQuery re-routes automatically.
    confirmVerificationOtp: builder.mutation<
      ApplicationListItem,
      { id: string; payload: OnboardingOtpVerifyRequest }
    >({
      query: ({ id, payload }) => ({
        url: `/onboarding/applications/${id}/verify/otp/confirm`,
        method: "POST",
        body: payload,
        auth: true,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "OnboardingApplication", id }],
    }),

    // Same call for Form 1's "2nd checkpoint" override (state READY_FOR_REVIEW,
    // before OTP verification) and Form 2's KYC-edit half (state VERIFIED) —
    // see lendcord-core's onboarding-review-flow.md, section 3.
    getApplicantProfile: builder.query<ApplicationProfile, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/profile`, auth: true }),
      providesTags: (result, error, id) => [{ type: "OnboardingApplication", id }],
    }),

    // state-advance is conditional server-side on when this is called (Form 1
    // vs. Form 2 — see the same doc, section 5); invalidating the row + LIST
    // tags is enough for the FE to just react to whatever state comes back.
    updateApplicantProfile: builder.mutation<ApplicationProfile, { id: string; payload: ApplicationProfileUpdateRequest }>({
      query: ({ id, payload }) => ({
        url: `/onboarding/applications/${id}/profile`,
        method: "PUT",
        body: payload,
        auth: true,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "OnboardingApplication", id },
        { type: "OnboardingApplication", id: "LIST" },
      ],
    }),

    // Always 200, null-shaped if unsaved — never skip this call.
    getLoanTerms: builder.query<LoanTermsResponse, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/loan-terms`, auth: true }),
      providesTags: (result, error, id) => [{ type: "OnboardingApplication", id }],
    }),

    updateLoanTerms: builder.mutation<LoanTermsResponse, { id: string; payload: LoanTermsRequest }>({
      query: ({ id, payload }) => ({
        url: `/onboarding/applications/${id}/loan-terms`,
        method: "PUT",
        body: payload,
        auth: true,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "OnboardingApplication", id }],
    }),

    // Form 3's trigger. Idempotent — the first call (or a retry after a
    // PENDING) invokes the provider; state only advances to CIBIL_VERIFICATION
    // once the provider was actually called, so invalidating the application's
    // tag lets ApplicationReviewFlow re-route to the right step automatically.
    checkCrif: builder.mutation<CrifCheckResult, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/crif/check`, method: "POST", auth: true }),
      invalidatesTags: (result, error, id) => [{ type: "OnboardingApplication", id }],
    }),

    // Read-only recall of an already-completed check — 404s (LEND-031) if the
    // check has never completed, which CrifStep.tsx treats as "not run yet"
    // rather than a real error (see the isQueryApiError check there).
    getCrifReport: builder.query<CrifCheckResult, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/crif/report`, auth: true }),
      providesTags: (result, error, id) => [{ type: "OnboardingApplication", id }],
    }),

    // keepUnusedDataFor: 0, same reasoning as profile documents' own download
    // URL query — a presigned URL expires, so caching it past the component
    // that requested it would serve a dead link.
    getCrifDownload: builder.query<CrifDownloadResponse, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/crif/download`, auth: true }),
      keepUnusedDataFor: 0,
    }),

    // Soft rejection — 204 response, but the request body now carries the
    // reviewer's reason (optional server-side, required by the FE form —
    // see ApplicationActions.tsx). status -> REJECTED, no data removed, state
    // untouched (see lendcord-core's onboarding-review-flow.md, section 7).
    // remarks overwrites the application's existing remarks field. Invalidating
    // both the row and LIST tags lets the listing's status badge/remarks
    // column and ApplicationReviewFlow's terminal-status check pick up the change.
    rejectApplication: builder.mutation<void, { id: string; remarks: string }>({
      query: ({ id, remarks }) => ({
        url: `/onboarding/applications/${id}/reject`,
        method: "POST",
        body: { remarks },
        auth: true,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "OnboardingApplication", id },
        { type: "OnboardingApplication", id: "LIST" },
      ],
    }),

    // Terminal approval — gated server-side on state CIBIL_VERIFICATION (a
    // completed CRIF check), see lendcord-core's OnboardingController.approve.
    // Unlike reject/delete, returns the updated application (status ->
    // APPROVED) instead of 204, but invalidating the same tags is enough —
    // ApplicationReviewFlow's terminal-status check picks up status ===
    // APPROVED off the resulting refetch and routes to the detail view.
    approveApplication: builder.mutation<ApplicationListItem, string>({
      query: (id) => ({ url: `/onboarding/applications/${id}/approve`, method: "POST", auth: true }),
      invalidatesTags: (result, error, id) => [
        { type: "OnboardingApplication", id },
        { type: "OnboardingApplication", id: "LIST" },
      ],
    }),

    // Also soft — status -> DELETED, same "no data removed", same
    // remarks-as-reason shape as reject above, just a different terminal status.
    deleteApplication: builder.mutation<void, { id: string; remarks: string }>({
      query: ({ id, remarks }) => ({
        url: `/onboarding/applications/${id}`,
        method: "DELETE",
        body: { remarks },
        auth: true,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "OnboardingApplication", id },
        { type: "OnboardingApplication", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useSubmitApplicationMutation,
  useListApplicationsQuery,
  useGetApplicationQuery,
  useSendVerificationOtpMutation,
  useConfirmVerificationOtpMutation,
  useGetApplicantProfileQuery,
  useUpdateApplicantProfileMutation,
  useGetLoanTermsQuery,
  useUpdateLoanTermsMutation,
  useCheckCrifMutation,
  useGetCrifReportQuery,
  useLazyGetCrifDownloadQuery,
  useRejectApplicationMutation,
  useApproveApplicationMutation,
  useDeleteApplicationMutation,
} = onboardingApi;
